import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Alert,
  ActivityIndicator,
  Modal,
  TextInput,
  RefreshControl,
} from 'react-native';
import { useNavigation, useRoute, useFocusEffect } from '@react-navigation/native';
import Header from '../../Components/Header';
import ScreenWrapper from '../../Components/ScreenWrapper';
import SpinWheel from '../../Components/SpinWheel';
import { theme } from '../../constants/theme';
import { useAuth } from '../../Utils/AuthProvider';
import { hp, wp } from '../../helpers/common';
import {
  fetchSpinWheelDetails,
  recordSpinResult,
  getUserSpinCount,
} from '../../services/spinWheelService';
import { updateUser, logDivasPointsChange, getUserData } from '../../services/userService';
import { getAndroidConfig, getConfig } from '../../services/configService';
import moment from 'moment';
import Avatar from '../../Components/Avatar';

import defaultUserImg from '../../../assets/images/defaultuser.png';

interface WheelOption {
  id: string;
  option_label: string;
  option_value: string;
  option_type: string;
  color?: string;
  probability_weight?: number;
  assigned_user?: {
    id: string;
    name?: string;
    image?: string;
  };
}

interface SpinWheelData {
  id: string;
  title: string;
  description?: string;
  created_by: string;
  created_at: string;
  created_by_user?: {
    id: string;
    name?: string;
    image?: string;
  };
  options: WheelOption[];
}

const SpinWheelScreen = () => {
  const navigation = useNavigation<any>();
  const route = useRoute<any>();
  const { userData, setUserData, refreshUserData } = useAuth();

  const [wheel, setWheel] = useState<SpinWheelData | null>(null);
  const [loading, setLoading] = useState(true);
  const [spinning, setSpinning] = useState(false);
  const [lastResult, setLastResult] = useState<any>(null);
  const [showResultModal, setShowResultModal] = useState(false);
  const [spinCount, setSpinCount] = useState(0);
  const [divasPoints, setDivasPointsState] = useState<number>(Number(userData?.divas_points ?? 0));
  const [bonusPoints, setBonusPointsState] = useState<number>(Number((userData as any)?.bonus_points ?? 0));
  const [favoriteNumber, setFavoriteNumber] = useState('');
  const [pointsChange, setPointsChange] = useState<{ delta: number; total: number; deltaBonus?: number; deltaDivas?: number } | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [androidConfig, setAndroidConfig] = useState<any>({});
  const [spinCost, setSpinCost] = useState(''); // User-inputted cost per spin

  const evaluateFormula = (formula: string | undefined, variables: Record<string, number>): number | null => {
    if (!formula) {
      return null;
    }

    let expression = formula;
    Object.entries(variables).forEach(([key, value]) => {
      const regex = new RegExp(key, 'g');
      expression = expression.replace(regex, value.toString());
    });

    expression = expression.replace(/[{}]/g, '');

    const sanitized = expression.replace(/[^0-9+\-*/().\s]/g, '');

    if (!sanitized.trim()) {
      return null;
    }

    try {
      const result = Function('"use strict"; return (' + sanitized + ');')();
      const numeric = Number(result);
      return Number.isFinite(numeric) ? numeric : null;
    } catch (error) {
      return null;
    }
  };

  const { wheelId } = route.params;

  // Load remote configuration on mount
  useEffect(() => {
    loadRemoteConfig();
  }, []);

  useEffect(() => {
    loadWheelDetails();
  }, [wheelId]);

  useEffect(() => {
    if (wheelId && userData?.id) {
      loadSpinCount();
    }
  }, [wheelId, userData?.id]);

  useEffect(() => {
    setDivasPointsState(Number(userData?.divas_points ?? 0));
    setBonusPointsState(Number((userData as any)?.bonus_points ?? 0));
  }, [userData?.divas_points, (userData as any)?.bonus_points]);

  // Auto-refresh user data when screen comes into focus
  useFocusEffect(
    React.useCallback(() => {
      if (refreshUserData) {
        refreshUserData();
      }
    }, [refreshUserData])
  );

  // Manual refresh function for pull-to-refresh
  const onRefresh = React.useCallback(async () => {
    setRefreshing(true);
    try {
      if (refreshUserData) {
        await refreshUserData();
      }
      // Also reload spin count
      if (wheelId && userData?.id) {
        await loadSpinCount();
      }
      // Reload config
      await loadRemoteConfig();
    } catch (error) {
      console.error('Error refreshing data:', error);
    } finally {
      setRefreshing(false);
    }
  }, [refreshUserData, wheelId, userData?.id]);

  const loadRemoteConfig = async () => {
    try {
      // Force refresh to get latest config from remote URL (bypass cache)
      const fullConfig: any = await getConfig(true); // Force refresh from https://dynamicdivas.in/info.json
      
      if (fullConfig && fullConfig.android) {
        const androidConfig = fullConfig.android;
        // Only use values from remote config - no local fallbacks
        setAndroidConfig({
          MinCost: androidConfig.MinCost,
          MaxCost: androidConfig.MaxCost,
          AddCredits: androidConfig.AddCredits,
          DeductCredits: androidConfig.DeductCredits,
          Adminid: androidConfig.Adminid,
        });
      } else {
      }
    } catch (error) {
      console.error('❌ Error loading remote config from https://dynamicdivas.in/info.json:', error);
    }
  };

  const loadWheelDetails = async () => {
    try {
      const result = await fetchSpinWheelDetails(wheelId);

      if (result.success) {
        const data = result.data as SpinWheelData;
        setWheel({
          ...data,
          options: data.options || [],
        });
      } else {
        Alert.alert('Error', result.msg);
      }
    } catch (error) {
      Alert.alert('Error', 'Could not load spin wheel');
    } finally {
      setLoading(false);
    }
  };

  const loadSpinCount = async () => {
    if (!userData?.id) return;

    try {
      const result = await getUserSpinCount(wheelId, userData.id);
      if (result.success) {
        setSpinCount(result.data);
      }
    } catch (error) {
      console.error('Error loading spin count:', error);
    }
  };

  const handleBeforeSpin = async () => {
    const trimmedFavorite = favoriteNumber.trim();

    if (!trimmedFavorite) {
      Alert.alert('Favourite Number Required', 'Please enter your favourite number before spinning.');
      return false;
    }

    const trimmedSpinCost = spinCost.trim();
    if (!trimmedSpinCost) {
      Alert.alert('Spin Cost Required', 'Please enter the number of Divas Points you want to use for this spin.');
      return false;
    }

    const costValue = Number(trimmedSpinCost);
    if (isNaN(costValue) || costValue <= 0) {
      Alert.alert('Invalid Spin Cost', 'Please enter a valid positive number for spin cost.');
      return false;
    }

    // Validate spin cost is within MinCost and MaxCost range
    const minCost = Number(androidConfig?.MinCost ?? 10);
    const maxCost = Number(androidConfig?.MaxCost ?? 1000);

    if (costValue < minCost) {
      Alert.alert('Spin Cost Too Low', `Minimum spin cost is ${minCost} Divas Points.`);
      return false;
    }

    if (costValue > maxCost) {
      Alert.alert('Spin Cost Too High', `Maximum spin cost is ${maxCost} Divas Points.`);
      return false;
    }

    const allowedRoles = ['special', 'admin', 'super_admin'];
    if (userData?.role && !allowedRoles.includes(userData.role)) {
      Alert.alert('Access Restricted', 'Only Special users can spin the wheel.');
      return false;
    }

    const bonusPoints = Number((userData as any)?.bonus_points ?? 0);
    const currentPoints = Number(userData?.divas_points ?? 0);
    const totalAvailable = bonusPoints + currentPoints;

    if (totalAvailable < costValue) {
      Alert.alert(
        'Insufficient Credits',
        `You need ${costValue} Divas Points for this spin. You currently have ${totalAvailable} (Bonus + Divas Points).`
      );
      return false;
    }

    setPointsChange(null);
    setSpinning(true);
    return true;
  };

  const handleSpinComplete = async (selectedOption: any) => {
    const normalizedFavorite = favoriteNumber.trim();
    const selectedValue = (selectedOption?.option_value ?? '').toString().trim();

    // Use current userData from context (kept fresh via focus effects / refresh)
    const currentBonus = Number((userData as any)?.bonus_points ?? 0);
    const currentDivas = Number(userData?.divas_points ?? 0);
    const costValue = Number(spinCost.trim());

    let newBonus = currentBonus;
    let newDivas = currentDivas;
    let deltaDivas = 0;
    let deltaBonus = 0; // Track bonus points change for display
    let transactionAmount = 0; // Track the transaction amount for logging
    let isWin = false;
    let winCredits = 0; // Gross win credits from AddCredits (for display)

    if (normalizedFavorite && selectedValue && normalizedFavorite === selectedValue) {
      isWin = true;

      // User won! First deduct the FULL bet amount (costValue), ignoring DeductCredits formula
      // Step 1: Deduct bet amount (from bonus_points first, then divas_points if needed)
      const deductAmount = costValue; // Always use full bet amount on win
      transactionAmount = deductAmount; // Amount bet

      let remainingToDeduct = deductAmount;

      // Deduct from bonus_points first
      const fromBonus = Math.min(remainingToDeduct, currentBonus);
      newBonus = Math.max(0, currentBonus - fromBonus);
      deltaBonus = newBonus - currentBonus; // Negative change to bonus points
      remainingToDeduct -= fromBonus;

      // Then deduct any remaining from divas_points
      if (remainingToDeduct > 0) {
        const fromDivas = Math.min(remainingToDeduct, currentDivas);
        newDivas = Math.max(0, currentDivas - fromDivas);
      } else {
        newDivas = currentDivas; // No change to divas if bonus was sufficient
      }

      // Step 2: Add win credits to Divas Points only
      const rawAdd = evaluateFormula(androidConfig?.AddCredits, {
        credits_required_For_SingleSpin: costValue,
        Totalcredits: newDivas,
      });
      const addAmount = rawAdd != null ? Math.round(rawAdd) : (costValue * 2); // Default: double the spin cost
      winCredits = addAmount;

      // Add win amount to divas points
      newDivas = newDivas + addAmount;
      
      // Calculate final deltas (net change to Divas Points)
      deltaDivas = newDivas - currentDivas;
    } else {
      // User lost. Deduct credits - bonus_points first, then divas_points
      const rawDeduct = evaluateFormula(androidConfig?.DeductCredits, {
        credits_required_For_SingleSpin: costValue,
        Totalcredits: currentDivas,
      }) ?? costValue; // Default: deduct the spin cost

      // Round off to nearest integer
      const deductAmount = Math.max(0, Math.round(rawDeduct));
      transactionAmount = deductAmount; // Amount bet/lost

      let remainingToDeduct = deductAmount;

      // Deduct from bonus_points first
      // Example: If bonus_points=500 and bet=80, then fromBonus=80, newBonus=420, deltaBonus=-80
      const fromBonus = Math.min(remainingToDeduct, currentBonus);
      newBonus = Math.max(0, currentBonus - fromBonus);
      deltaBonus = newBonus - currentBonus; // Negative change to bonus points (e.g., -80 means decreased by 80)
      remainingToDeduct -= fromBonus;

      // Then deduct any remaining from divas_points only if bonus_points were insufficient
      // If remainingToDeduct > 0, it means we need to deduct from divas_points
      // If remainingToDeduct = 0, divas_points remain unchanged (deltaDivas = 0)
      if (remainingToDeduct > 0) {
        const fromDivas = Math.min(remainingToDeduct, currentDivas);
        newDivas = Math.max(0, currentDivas - fromDivas);
      }
      // Note: If remainingToDeduct = 0, newDivas stays as currentDivas (no change)

      deltaDivas = newDivas - currentDivas; // Negative or zero change applied to Divas Points (0 if no change)
    }

    setLastResult(selectedOption);
    setShowResultModal(true);
    setSpinning(false);
    
    // Clear the inputs after spin
    setFavoriteNumber('');
    setSpinCost('');

    const userIdToUpdate = userData?.id;
    if (userIdToUpdate) {
      try {
        // Persist both bonus_points and divas_points
        const updateResult = await updateUser(userIdToUpdate, {
          bonus_points: newBonus,
          divas_points: newDivas,
        });

        if (!updateResult.success) {
          console.error('[SpinWheel] Failed to update user points:', updateResult.msg);
          Alert.alert('Update Error', `Failed to update points: ${updateResult.msg}`);
          return;
        }


        // Update local auth state
        setUserData?.((prev: any) =>
          prev
            ? {
                ...prev,
                bonus_points: newBonus,
                divas_points: newDivas,
              }
            : prev
        );
        setDivasPointsState(newDivas);
        setBonusPointsState(newBonus);
        
        // Log the points change to divas_points_logs table ONLY when there are actual changes
        // entry_source = 2 for Spin Wheel
        // Note: amount field is always the transaction amount (bet or win), independent of which points changed
        const userIntegerId = userData?.user_id; // bigint user_id column
        if (userIntegerId) {
          // Only create log entry if there are actual changes to bonus_points or divas_points
          // This ensures we don't create unnecessary log entries with zero values
          const hasDivasChange = deltaDivas !== 0;
          const hasBonusChange = deltaBonus !== 0;
          
          if (hasDivasChange || hasBonusChange) {
            // Log the changes:
            // - divas_points: change in divas points (0 if no change)
            // - bonus_points: change in bonus points (0 if no change)
            // - amount: not updated (left as default/unchanged)
          await logDivasPointsChange(
            userIntegerId,
              deltaDivas, // the change amount (0 if no change, positive/negative if changed)
            2, // entry_source = 2 for Spin Wheel
              0, // created_by (0 for system)
              deltaBonus // the change amount (0 if no change, positive/negative if changed)
              // amount parameter removed - not updating amount column
          );
          }
        }
      } catch (error) {
        console.error('[SpinWheel] Error updating user points:', error);
        Alert.alert('Error', 'Failed to update points. Please try again.');
      }
    }

    // Set points change for display
    // For wins, show the gross win credits from AddCredits (winCredits)
    // For losses, show the net divas change if any, otherwise show bonus change
    const displayDelta =
      isWin && winCredits > 0
        ? winCredits
        : (deltaDivas !== 0 ? deltaDivas : deltaBonus);
    setPointsChange({ 
      delta: displayDelta, 
      total: newDivas,
      deltaBonus: deltaBonus,
      deltaDivas: deltaDivas
    });

    const userIdToUse = userData?.id;
    if (userIdToUse) {
      try {
        await recordSpinResult(wheelId, selectedOption.id, userIdToUse);
      setSpinCount(prev => prev + 1);
    } catch (error) {
      console.error('Error recording spin result:', error);
      }
    }
  };

  const handleCloseModal = () => {
    setShowResultModal(false);
  };


  if (loading) {
    return (
      <ScreenWrapper>
        <Header title="Spin Wheel" showBackButton />
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={theme.colors.primary} />
          <Text style={styles.loadingText}>Loading wheel...</Text>
        </View>
      </ScreenWrapper>
    );
  }

  if (!wheel) {
    return (
      <ScreenWrapper>
        <Header title="Spin Wheel" showBackButton />
        <View style={styles.errorContainer}>
          <Text style={styles.errorText}>Wheel not found</Text>
        </View>
      </ScreenWrapper>
    );
  }

  return (
    <ScreenWrapper>
      <Header title="Spin Wheel" showBackButton />
      
      <ScrollView 
        style={styles.container} 
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            colors={[theme.colors.primary]}
            tintColor={theme.colors.primary}
          />
        }
      >
        {/* Wheel Header */}
        <View style={styles.header}>
          <View style={styles.userInfo}>
            <Avatar
              uri={wheel.created_by_user?.image || defaultUserImg}
              size={50}
            />
            <View style={styles.userDetails}>
              <Text style={styles.userName}>{wheel.created_by_user?.name || 'Unknown User'}</Text>
              <Text style={styles.time}>
                Created {moment(wheel.created_at).fromNow()}
              </Text>
            </View>
          </View>
        </View>

        {/* Wheel Title */}
        <Text style={styles.title}>{wheel.title}</Text>
        
        {/* Description */}
        {wheel.description && (
          <Text style={styles.description}>{wheel.description}</Text>
        )}

        {/* Spin Count */}
        <View style={styles.statsContainer}>
          <View style={styles.statBox}>
            <Text style={styles.statNumber}>{spinCount}</Text>
            <Text style={styles.statLabel}>Total Spins</Text>
          </View>
          <View style={styles.statBox}>
            <Text style={styles.statNumber}>{divasPoints}</Text>
            <Text style={styles.statLabel}>Divas Points</Text>
          </View>
          <View style={styles.statBox}>
            <Text style={styles.statNumber}>{bonusPoints}</Text>
            <Text style={styles.statLabel}>Bonus Points</Text>
          </View>
        </View>

        {/* User ID Display */}
        <View style={styles.userIdContainer}>
          <Text style={styles.userIdLabel}>User ID</Text>
          <Text style={styles.userIdValue}>
            {userData?.user_id ?? userData?.id ?? 'Unknown'}
          </Text>
        </View>

        {/* Favourite Number Input */}
        <View style={styles.favoriteContainer}>
          <Text style={styles.favoriteLabel}>Enter Your Favourite Number</Text>
          <TextInput
            style={styles.favoriteInput}
            value={favoriteNumber}
            onChangeText={setFavoriteNumber}
            placeholder="e.g. 7"
            keyboardType="numeric"
            maxLength={10}
          />
          <Text style={styles.favoriteHint}>
            Match this number to earn bonus Divas Points.
          </Text>
        </View>

        {/* Spin Cost Input */}
        <View style={styles.favoriteContainer}>
          <Text style={styles.favoriteLabel}>Enter Spin Cost (Divas Points)</Text>
          <TextInput
            style={styles.favoriteInput}
            value={spinCost}
            onChangeText={setSpinCost}
            placeholder={`Between ${androidConfig?.MinCost ?? 10} - ${androidConfig?.MaxCost ?? 1000}`}
            keyboardType="numeric"
            maxLength={10}
          />
          <Text style={styles.favoriteHint}>
            Allowed range: {androidConfig?.MinCost ?? 10} - {androidConfig?.MaxCost ?? 1000} Divas Points
          </Text>
          <Text style={styles.favoriteHint}>
            Win: Earn bonus credits (formula applied). Lose: Lose your spin cost.
          </Text>
        </View>

        {/* Spin Wheel Component */}
        {wheel.options && wheel.options.length > 0 ? (
          <View style={styles.wheelContainer}>
            <SpinWheel
              options={wheel.options}
              onSpinComplete={handleSpinComplete}
              disabled={spinning}
              onBeforeSpin={handleBeforeSpin}
            />
          </View>
        ) : (
          <View style={styles.noOptionsContainer}>
            <Text style={styles.noOptionsText}>No options available</Text>
          </View>
        )}
      </ScrollView>

      {/* Result Modal */}
      <Modal
        visible={showResultModal}
        transparent
        animationType="fade"
        onRequestClose={handleCloseModal}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>Spin Result!</Text>
            
            {lastResult && (
              <View style={styles.resultContainer}>
                <View
                  style={[
                    styles.resultCircle,
                    { backgroundColor: lastResult.color || theme.colors.primary },
                  ]}
                >
                  <Text style={styles.resultCircleText}>
                    {lastResult.option_type === 'number'
                      ? lastResult.option_value
                      : lastResult.option_label.substring(0, 2).toUpperCase()}
                  </Text>
                </View>
                <Text style={styles.resultLabel}>{lastResult.option_label}</Text>
                <Text style={styles.resultValue}>
                  {lastResult.option_type === 'number'
                    ? `Value: ${lastResult.option_value}`
                    : lastResult.option_value}
                </Text>
                {lastResult.assigned_user && (
                  <Text style={styles.resultAssignedUser}>
                    Assigned to: {lastResult.assigned_user.name || lastResult.assigned_user.id}
                  </Text>
                )}
                {pointsChange && (
                  <View style={styles.pointsChangeContainer}>
                    {pointsChange.deltaBonus !== undefined && pointsChange.deltaBonus < 0 && pointsChange.deltaDivas === 0 && (
                      <Text
                        style={[
                          styles.pointsChangeText,
                          styles.pointsLossText,
                        ]}>
                        {pointsChange.deltaBonus}
                        {' '}
                        Bonus Points
                      </Text>
                    )}
                    {pointsChange.deltaDivas !== undefined && pointsChange.deltaDivas !== 0 && (
                    <Text
                      style={[
                        styles.pointsChangeText,
                          pointsChange.deltaDivas >= 0
                          ? styles.pointsGainText
                          : styles.pointsLossText,
                      ]}>
                        {pointsChange.deltaDivas >= 0 ? '+' : ''}
                        {pointsChange.deltaDivas}
                      {' '}
                      Divas Points
                    </Text>
                    )}
                    {pointsChange.deltaBonus !== undefined && pointsChange.deltaBonus < 0 && pointsChange.deltaDivas !== 0 && (
                      <Text
                        style={[
                          styles.pointsChangeText,
                          styles.pointsLossText,
                        ]}>
                        {' '}
                        ({pointsChange.deltaBonus} Bonus Points)
                      </Text>
                    )}
                    <Text style={styles.pointsTotalText}>
                      New Total: {pointsChange.total} Divas Points
                    </Text>
                  </View>
                )}
              </View>
            )}

            <TouchableOpacity
              style={styles.modalButton}
              onPress={handleCloseModal}
            >
              <Text style={styles.modalButtonText}>OK</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>
    </ScreenWrapper>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingHorizontal: wp(4),
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 10,
    color: theme.colors.text,
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  errorText: {
    color: theme.colors.text,
    fontSize: 16,
  },
  header: {
    paddingVertical: hp(2),
  },
  userInfo: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  userDetails: {
    marginLeft: 10,
  },
  userName: {
    fontSize: 16,
    fontWeight: 'bold',
    color: theme.colors.text,
  },
  time: {
    fontSize: 12,
    color: theme.colors.textLight,
    marginTop: 2,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    color: theme.colors.text,
    marginBottom: hp(1),
  },
  description: {
    fontSize: 14,
    color: theme.colors.textLight,
    marginBottom: hp(2),
  },
  statsContainer: {
    flexDirection: 'row',
    marginBottom: hp(2),
  },
  statBox: {
    flex: 1,
    backgroundColor: theme.colors.background,
    padding: 15,
    borderRadius: 10,
    alignItems: 'center',
    marginHorizontal: 5,
  },
  statNumber: {
    fontSize: 24,
    fontWeight: 'bold',
    color: theme.colors.primary,
  },
  statLabel: {
    fontSize: 12,
    color: theme.colors.textLight,
    marginTop: 5,
  },
  favoriteContainer: {
    marginBottom: hp(2),
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 4,
  },
  favoriteLabel: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.text,
    marginBottom: 6,
  },
  favoriteInput: {
    borderWidth: 1,
    borderColor: theme.colors.primary,
    borderRadius: 10,
    paddingHorizontal: 12,
    paddingVertical: 8,
    fontSize: 16,
    color: theme.colors.text,
  },
  favoriteHint: {
    marginTop: 6,
    fontSize: 12,
    color: theme.colors.textLight,
  },
  userIdContainer: {
    marginBottom: hp(1.5),
    backgroundColor: theme.colors.background,
    padding: 12,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: theme.colors.primary + '33',
  },
  userIdLabel: {
    fontSize: 12,
    color: theme.colors.textLight,
  },
  userIdValue: {
    marginTop: 4,
    fontSize: 14,
    color: theme.colors.text,
  },
  wheelContainer: {
    marginVertical: hp(2),
  },
  noOptionsContainer: {
    paddingVertical: hp(4),
    alignItems: 'center',
  },
  noOptionsText: {
    fontSize: 16,
    color: theme.colors.textLight,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    backgroundColor: 'white',
    borderRadius: 20,
    padding: 30,
    width: '80%',
    alignItems: 'center',
  },
  modalTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 20,
    color: theme.colors.text,
  },
  resultContainer: {
    alignItems: 'center',
    marginBottom: 20,
  },
  resultCircle: {
    width: 100,
    height: 100,
    borderRadius: 50,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 15,
  },
  resultCircleText: {
    fontSize: 36,
    fontWeight: 'bold',
    color: 'white',
  },
  resultLabel: {
    fontSize: 18,
    fontWeight: '600',
    color: theme.colors.text,
    marginBottom: 5,
  },
  resultValue: {
    fontSize: 14,
    color: theme.colors.textLight,
  },
  resultAssignedUser: {
    fontSize: 13,
    color: theme.colors.text,
    marginTop: 6,
  },
  pointsChangeContainer: {
    marginTop: 12,
    alignItems: 'center',
  },
  pointsChangeText: {
    fontSize: 18,
    fontWeight: 'bold',
  },
  pointsGainText: {
    color: '#2ecc71',
  },
  pointsLossText: {
    color: '#e74c3c',
  },
  pointsTotalText: {
    marginTop: 4,
    fontSize: 14,
    color: theme.colors.text,
  },
  modalButton: {
    backgroundColor: theme.colors.primary,
    paddingHorizontal: 40,
    paddingVertical: 12,
    borderRadius: 25,
    marginTop: 10,
  },
  modalButtonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: 'bold',
  },
});

export default SpinWheelScreen;

